:root {
  color-scheme: light;
  font-family:
    Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #171814;
  background: #f5f7f4;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  --ink: #171814;
  --muted: #687069;
  --line: #dfe5df;
  --paper: #ffffff;
  --wash: #f5f7f4;
  --green: #18875f;
  --green-soft: #dcefe7;
  --red: #bd4237;
  --red-soft: #f7dfdc;
  --amber: #b36b00;
  --teal: #0f766e;
  --violet: #6554c0;
  --shadow: 0 18px 50px rgba(23, 24, 20, 0.08);
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  min-width: 320px;
  background:
    radial-gradient(circle at 18% 12%, rgba(24, 135, 95, 0.1), transparent 28rem),
    linear-gradient(180deg, #f5f7f4 0%, #ffffff 48%, #f5f7f4 100%);
}

button,
input {
  font: inherit;
}

a {
  color: inherit;
  text-decoration: none;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  padding: 0 28px;
  border-bottom: 1px solid rgba(223, 229, 223, 0.92);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px);
}

.brand,
.topbar-meta,
.eyebrow,
.freshness,
.primary-link,
.secondary-link,
.note-row,
.event-foot a {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.brand {
  font-weight: 800;
  letter-spacing: 0;
}

.brand svg {
  color: var(--green);
}

.topbar-meta {
  color: var(--muted);
  font-size: 14px;
}

.content-grid,
.section-head,
.profile-grid,
.source-layout,
.table-band,
.profiles-band,
.content-band {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
}

.hero-band {
  padding: 70px 0 42px;
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.06fr) minmax(340px, 0.76fr);
  gap: 34px;
  align-items: stretch;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 420px;
}

.eyebrow,
.section-kicker,
.panel-label {
  color: var(--teal);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 760px;
  margin: 16px 0 18px;
  font-size: clamp(40px, 8vw, 86px);
  line-height: 0.98;
  letter-spacing: 0;
}

.hero-copy p {
  max-width: 730px;
  margin: 0;
  color: #3c433d;
  font-size: 19px;
  line-height: 1.65;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 30px;
}

.primary-link,
.secondary-link {
  min-height: 46px;
  padding: 0 18px;
  border-radius: 8px;
  font-weight: 800;
}

.primary-link {
  background: var(--ink);
  color: white;
}

.secondary-link {
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.82);
  color: var(--ink);
}

.signal-panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 420px;
  padding: 24px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  box-shadow: var(--shadow);
}

.signal-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.signal-top strong {
  display: block;
  margin-top: 6px;
  font-size: 62px;
  line-height: 1;
}

.status-pill,
.tier {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  background: #edf3ee;
  color: var(--teal);
  font-size: 12px;
  font-weight: 800;
}

.signal-bars {
  display: grid;
  gap: 16px;
  margin: 24px 0;
}

.bar-row {
  display: grid;
  grid-template-columns: 76px minmax(96px, 1fr) 32px;
  gap: 12px;
  align-items: center;
  color: var(--muted);
  font-size: 14px;
}

.bar-row b {
  color: var(--ink);
  text-align: right;
}

.bar-track {
  height: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: #eef1ee;
}

.bar-fill {
  height: 100%;
  border-radius: inherit;
}

.bar-fill.ark {
  background: var(--green);
}

.bar-fill.sec {
  background: var(--violet);
}

.bar-fill.sell {
  background: var(--red);
}

.mini-feed {
  display: grid;
  gap: 10px;
}

.mini-feed a {
  display: grid;
  grid-template-columns: 10px 1fr auto;
  gap: 10px;
  align-items: center;
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  color: #3c433d;
  font-size: 14px;
}

.mini-feed b {
  color: var(--ink);
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.dot.positive {
  background: var(--green);
}

.dot.negative {
  background: var(--red);
}

.summary-band {
  padding: 18px 0 34px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.metric {
  min-height: 132px;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
}

.metric span,
.metric small {
  display: block;
  color: var(--muted);
}

.metric strong {
  display: block;
  margin: 14px 0 8px;
  font-size: 42px;
  line-height: 1;
}

.content-band,
.profiles-band,
.table-band,
.sources-band {
  padding: 46px 0;
}

.section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}

.section-head h2,
.source-layout h2 {
  margin: 6px 0 0;
  font-size: clamp(28px, 4vw, 46px);
  line-height: 1.06;
  letter-spacing: 0;
}

.search-cluster {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(320px, 100%);
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
}

.search-box svg {
  color: var(--muted);
  flex: 0 0 auto;
}

.search-box input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--ink);
}

.segmented {
  display: inline-flex;
  min-height: 44px;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
}

.segmented button {
  min-width: 54px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  font-weight: 800;
}

.segmented button.active {
  background: var(--ink);
  color: #fff;
}

.event-grid {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.event-card,
.profile-card {
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  box-shadow: 0 10px 32px rgba(23, 24, 20, 0.04);
}

.event-card {
  display: flex;
  flex-direction: column;
  min-height: 300px;
  padding: 18px;
}

.event-card-top,
.event-foot,
.profile-top,
.profile-main,
.profile-bottom,
.profile-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.direction {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
}

.direction.positive {
  background: var(--green-soft);
  color: var(--green);
}

.direction.negative {
  background: var(--red-soft);
  color: var(--red);
}

.direction.compact {
  min-height: 26px;
}

.event-card h3 {
  margin: 22px 0 10px;
  font-size: 22px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.event-card p {
  margin: 0;
  color: #515a52;
  line-height: 1.58;
}

.event-numbers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: auto;
  padding-top: 22px;
}

.event-numbers div {
  min-height: 72px;
  padding: 12px;
  border-radius: 8px;
  background: #f6f8f6;
}

.event-numbers span,
.profile-main small {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.event-numbers b,
.profile-main strong {
  display: block;
  margin-top: 8px;
  font-size: 21px;
  overflow-wrap: anywhere;
}

.event-foot {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--line);
  color: var(--muted);
  font-size: 13px;
}

.event-foot a {
  color: var(--teal);
  font-weight: 800;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.profile-card {
  display: flex;
  flex-direction: column;
  min-height: 244px;
  padding: 16px;
}

.profile-card h3 {
  margin: 0;
  font-size: 19px;
}

.profile-card p {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.35;
}

.profile-meta {
  justify-content: flex-start;
  flex-wrap: wrap;
  margin: 18px 0;
}

.profile-meta span {
  min-height: 28px;
  padding: 6px 9px;
  border-radius: 999px;
  background: #f1f4f1;
  color: #4f5b52;
  font-size: 12px;
}

.profile-main {
  align-items: stretch;
  margin-top: auto;
}

.profile-main div {
  flex: 1;
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
}

.profile-bottom {
  align-items: flex-start;
  flex-direction: column;
  margin-top: 14px;
  color: var(--muted);
  font-size: 13px;
}

.freshness {
  color: var(--muted);
  font-size: 14px;
}

.table-shell {
  overflow: auto;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  box-shadow: var(--shadow);
}

table {
  width: 100%;
  min-width: 880px;
  border-collapse: collapse;
}

th,
td {
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
}

th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f7faf7;
  color: var(--muted);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0;
}

td {
  color: #313832;
}

td strong,
td span {
  display: block;
}

td span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 13px;
}

td a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--teal);
  font-weight: 800;
}

.sources-band {
  background: #18211c;
  color: #f8fbf8;
}

.source-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.84fr) minmax(320px, 1fr);
  gap: 44px;
  align-items: start;
}

.source-layout p {
  color: #d3ddd4;
  line-height: 1.7;
}

.note-list {
  display: grid;
  gap: 12px;
}

.note-row {
  min-height: 58px;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: #edf4ed;
}

.note-row svg {
  flex: 0 0 auto;
  color: #6be2b2;
}

.note-row.warning svg {
  color: #f3b755;
}

.empty-state,
.loading-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 12px;
  color: var(--muted);
  font-weight: 800;
}

.empty-state {
  width: min(1180px, calc(100% - 40px));
  min-height: 180px;
  margin: 0 auto;
  border: 1px dashed var(--line);
  border-radius: 8px;
  background: var(--paper);
}

.loading-shell.error {
  color: var(--red);
}

.spin {
  animation: spin 1.1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 980px) {
  .topbar {
    padding: 0 18px;
  }

  .topbar-meta {
    display: none;
  }

  .hero-grid,
  .source-layout {
    grid-template-columns: 1fr;
  }

  .hero-copy {
    min-height: auto;
  }

  .signal-panel {
    min-height: 360px;
  }

  .stats-grid,
  .event-grid,
  .profile-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .section-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .search-cluster {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .content-grid,
  .section-head,
  .profile-grid,
  .source-layout,
  .table-band,
  .profiles-band,
  .content-band,
  .event-grid {
    width: min(100% - 28px, 1180px);
  }

  .hero-band {
    padding-top: 42px;
  }

  .hero-copy h1 {
    font-size: 42px;
  }

  .hero-copy p {
    font-size: 16px;
  }

  .stats-grid,
  .event-grid,
  .profile-grid {
    grid-template-columns: 1fr;
  }

  .search-box,
  .segmented {
    width: 100%;
  }

  .segmented button {
    flex: 1;
  }

  .event-numbers {
    grid-template-columns: 1fr;
  }

  .source-layout {
    gap: 24px;
  }
}
